import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { CodeInput } from "./CodeInput";

/**
 * Generic 2-step verification dialog.
 *
 * Props:
 *   open                  : boolean
 *   onOpenChange(open)    : function
 *   email                 : string (where the code was sent)
 *   onSubmit(code) -> Promise: called with the entered code; should throw on failure
 *   onSuccess()           : called after submit resolves successfully
 *   title, description    : strings (already i18n-translated by caller)
 *   testId                : string prefix
 */
export const VerifyCodeDialog = ({
    open, onOpenChange, email,
    onSubmit, onSuccess,
    title, description,
    testId = "verify-dialog",
}) => {
    const { t } = useTranslation();
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);

    const reset = () => { setCode(""); setBusy(false); };

    const handle = async (e) => {
        e.preventDefault();
        if (code.length !== 6) return;
        setBusy(true);
        try {
            const result = await onSubmit(code);
            toast.success(t("common.success"));
            onSuccess?.(result);
            onOpenChange(false);
            reset();
        } catch (err) {
            const detail = err?.response?.data?.detail || "";
            if (detail.toLowerCase().includes("invalid code")) toast.error(t("auth.invalid_code"));
            else if (detail.toLowerCase().includes("expired")) toast.error(t("auth.code_expired"));
            else if (detail.toLowerCase().includes("attempts")) toast.error(t("auth.too_many_attempts"));
            else toast.error(t("common.error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}
        >
            <DialogContent className="sm:max-w-md" data-testid={testId}>
                <DialogHeader>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-xs font-semibold w-fit mb-2">
                        <MailCheck className="h-3.5 w-3.5" />
                        {t("auth.code_sent_short")}
                    </div>
                    <DialogTitle className="font-heading font-black text-2xl">
                        {title || t("auth.enter_code_title")}
                    </DialogTitle>
                    <DialogDescription className="font-medium">
                        {description || t("auth.enter_code_subtitle", { email })}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handle} className="space-y-6 pt-2">
                    <CodeInput value={code} onChange={setCode} testIdPrefix={`${testId}-code`} />
                    <button
                        type="submit"
                        data-testid={`${testId}-submit`}
                        disabled={busy || code.length !== 6}
                        className="btn-primary w-full disabled:opacity-50"
                    >
                        {busy ? t("common.loading") : t("auth.verify")}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
};
